import React, { useState, useEffect, useMemo } from "react";
import http from "../../../api/http";
import { swalError } from "../../../utils/swalAlert.js";
import "../../../styles/AdminPanel/customerList.css";
import moment from "jalali-moment";
import { convertToPersianDigits } from "../../../utils/stringUtil.js";

const IconSearch = ({ size = 15, className }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export default function CustomerList() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    http
      .get("/auth/admin/customers/")
      .then((res) => setCustomers(res.data))
      .catch(() =>
        swalError({ title: "خطا", text: "عدم دسترسی به لیست مشتریان" }),
      );
  }, []);

  // فیلتر کردن
  const filteredCustomers = useMemo(() => {
    return customers.filter(
      (c) => c.name?.includes(search) || c.phone?.includes(search),
    );
  }, [customers, search]);

  // صفحه‌بندی
  const totalPages = Math.max(
    1,
    Math.ceil(filteredCustomers.length / itemsPerPage),
  );
  const pageSafe = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (currentPage !== pageSafe) setCurrentPage(pageSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCustomers.length]);

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const paginatedData = useMemo(() => {
    const start = (pageSafe - 1) * itemsPerPage;
    return filteredCustomers.slice(start, start + itemsPerPage);
  }, [filteredCustomers, pageSafe]);

  return (
    <div className="admin-page">
      <h1>مدیریت بانک مشتریان ({filteredCustomers.length} مورد)</h1>

      <div className="admin-search-box">
        <input
          placeholder="جستجو بر اساس نام یا موبایل..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }} // با جستجوی جدید به صفحه ۱ برو
        />
        <button type="button">
          <IconSearch />
        </button>
      </div>

      <div className="admin-customers-table-wrapper">
        <table className="admin-customers-table">
          <thead>
            <tr>
              <th>نام</th>
              <th>موبایل</th>
              <th>استان</th>
              <th>شهر</th>
              <th>کد پستی</th>
              <th>فروشگاه</th>
              <th>آدرس</th>
              <th>تاریخ ایجاد</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center" }}>
                  داده‌ای وجود ندارد
                </td>
              </tr>
            ) : (
              paginatedData.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.phone}</td>
                  <td>{c.province || "-"}</td>
                  <td>{c.city || "-"}</td>
                  <td>{c.postal_code || "-"}</td>
                  <td>
                    <span className="admin-badge">{c.store_name}</span>
                  </td>
                  <td style={{ fontSize: "0.85em", maxWidth: "200px" }}>
                    {c.address || "-"}
                  </td>
                  <td>
                    {convertToPersianDigits(
                      moment(c.created_at).format("HH:MM - jYYYY/jMM/jDD"),
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* ---- صفحه‌بندی ---- */}
        {filteredCustomers.length > 0 && (
          <div className="admin-pagination">
            <span className="admin-pagination-info">
              {filteredCustomers.length.toLocaleString("fa-IR")} مشتری — صفحه{" "}
              {pageSafe.toLocaleString("fa-IR")} از{" "}
              {totalPages.toLocaleString("fa-IR")}
            </span>
            <div className="admin-pagination-controls">
              <button
                type="button"
                className="admin-page-btn"
                disabled={pageSafe === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                aria-label="صفحه قبل"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              {pageNumbers.map((n) => (
                <button
                  type="button"
                  key={n}
                  className={`admin-page-btn${n === pageSafe ? " admin-page-btn--active" : ""}`}
                  onClick={() => setCurrentPage(n)}
                >
                  {n.toLocaleString("fa-IR")}
                </button>
              ))}
              <button
                type="button"
                className="admin-page-btn"
                disabled={pageSafe === totalPages}
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                aria-label="صفحه بعد"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
